import { useNavigate } from 'react-router-dom';
import { useMemo } from 'react';
import { Icon } from '@/deploy/components/ui/Icon';
import { Button } from '@/deploy/components/ui/Button';
import { Badge } from '@/deploy/components/ui/Badge';
import { PipelineTimeline } from '@/deploy/components/dashboard/PipelineTimeline';
import { DeploymentItem } from '@/deploy/components/dashboard/DeploymentItem';
import { useSessionStore } from '@/deploy/stores/session.store';
import { useDeploymentsStore } from '@/deploy/stores/deployments.store';
import { MOCK_APIS, MOCK_BETA_SLOTS, MOCK_CONTAINERS, MOCK_INSTANCES } from '@/deploy/data/mock';
import styles from './Dashboard.module.scss';

export function Dashboard() {
  const navigate = useNavigate();
  const project = useSessionStore((s) => s.currentProject());
  const deployments = useDeploymentsStore((s) => s.deployments);

  const projectDeployments = useMemo(
    () => deployments.filter((d) => d.projectId === project.id).slice(0, 5),
    [deployments, project.id],
  );

  const containers   = MOCK_CONTAINERS.filter((c) => c.projectId === project.id);
  const instances    = MOCK_INSTANCES.filter((i) => i.projectId === project.id);
  const apis         = MOCK_APIS.filter((a) => a.projectId === project.id);

  const prodInstances = instances.filter((i) => i.environment === 'prod');

  // Instances dont la version tournante diverge de la version déployée sur leur env
  const driftedInstances = prodInstances.filter((i) => {
    const c = containers.find((c) => c.id === i.containerId);
    return c && c.deployedVersions[i.environment] && i.runningVersion !== c.deployedVersions[i.environment];
  });

  // Slots bêta (utilisé par le raccourci « Slots Bêta »)
  const betaSlots = MOCK_BETA_SLOTS.filter((s) => s.projectId === project.id);
  const betaOccupied = betaSlots.filter((s) => s.allocated).length;

  return (
    <div className={styles.page}>
      <section className={styles.section}>
        <header className={styles.sectionHeader}>
          <div>
            <h2>Pipeline de déploiement</h2>
            <p>Cliquer sur un environnement pour voir ses containers et instances.</p>
          </div>
        </header>
        <PipelineTimeline />
      </section>

      <section className={styles.twoCols}>
        <div className={styles.section}>
          <header className={styles.sectionHeader}>
            <div>
              <h2>Déploiements récents</h2>
              <p>5 dernières MEP sur ce projet.</p>
            </div>
            <Button variant="ghost" size="sm" rightIcon={<Icon name="arrow-right" size={14} />} onClick={() => navigate('/deploiement/history')}>
              Voir tout
            </Button>
          </header>
          <div className={styles.list}>
            {projectDeployments.length > 0 ? (
              projectDeployments.map((d) => <DeploymentItem key={d.id} deployment={d} />)
            ) : (
              <div className={styles.empty}>Aucun déploiement sur ce projet pour l'instant.</div>
            )}
          </div>
        </div>

        <div className={styles.section}>
          <header className={styles.sectionHeader}>
            <div>
              <h2>Raccourcis</h2>
              <p>Actions fréquentes pour ce projet.</p>
            </div>
          </header>
          <div className={styles.shortcuts}>
            <ShortcutBtn onClick={() => navigate('/deploiement/environments/preprod')} icon="rocket" title="Pré-prod → Prod" desc="Promouvoir Pré-prod en 1 clic (copie exacte)" highlighted />
            <ShortcutBtn onClick={() => navigate('/deploiement/deploy')} icon="zap" title="Nouveau déploiement" desc="Construire Pré-prod from scratch" />
            <ShortcutBtn onClick={() => navigate('/deploiement/environments/dev')} icon="sparkles" title="Créer un env de dev" desc="One-shot, 1 service MC au choix" />
            <ShortcutBtn onClick={() => navigate('/deploiement/environments/beta')} icon="layers" title="Slots Bêta" desc={`${betaOccupied}/5 slots A..E occupés`} />
            <ShortcutBtn onClick={() => navigate('/deploiement/containers')} icon="package" title="Containers MC" desc={`${containers.length} types · ${apis.length} APIs`} />
            <ShortcutBtn onClick={() => navigate('/deploiement/logs')} icon="eye" title="Logs" desc="Live · instances + APIs" />
          </div>
          {driftedInstances.length > 0 && (
            <div className={styles.driftBox}>
              <header>
                <Icon name="warn" size={14} /> Instances en retard en prod
                <Badge tone="warning">{driftedInstances.length}</Badge>
              </header>
              <ul>
                {driftedInstances.slice(0, 4).map((i) => {
                  const c = containers.find((c) => c.id === i.containerId);
                  return (
                    <li key={i.id}>
                      <span className={styles.mono}>{i.name}</span>
                      <span className={styles.driftVers}>
                        <code>{i.runningVersion}</code>
                        <Icon name="arrow-right" size={10} />
                        <code>{c?.deployedVersions.prod}</code>
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

interface ShortcutBtnProps {
  onClick: () => void;
  icon: Parameters<typeof Icon>[0]['name'];
  title: string;
  desc: string;
  highlighted?: boolean;
}
function ShortcutBtn({ onClick, icon, title, desc, highlighted }: ShortcutBtnProps) {
  return (
    <button className={[styles.shortcut, highlighted && styles.highlightedShortcut].filter(Boolean).join(' ')} onClick={onClick}>
      <span className={styles.shortcutIcon}><Icon name={icon} size={18} /></span>
      <span className={styles.shortcutText}>
        <span className={styles.shortcutTitle}>{title}</span>
        <span className={styles.shortcutDesc}>{desc}</span>
      </span>
      <Icon name="arrow-right" size={14} />
    </button>
  );
}
